import _asyncToGenerator from "/home/runner/work/mainrepo/mainrepo/node_modules/.pnpm/@babel+runtime@7.29.2/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { getClassName } from '@core/shared/class-name';
import { CLASS_VALIDATOR_GLOBAL_CONFIG_FORMBUILDER, CrudAction, DEFAULT_EDITOR_DEFINITION_ID, EditorModelFactory, getSubModelMeta, Model } from '@core/shared/model';
import { distinctUntilChangedDeep, objectCloneDeep, temporaryToggle } from '@core/shared/utils';
import { BehaviorSubject, of, race, Subject, throwError, timer } from 'rxjs';
import { catchError, debounceTime, delayWhen, distinctUntilChanged, filter, map, pairwise, share, skipUntil, skipWhile, startWith, switchMap, take, tap } from 'rxjs/operators';
import { DynamicFormGroup } from '../dynamic-form-group';
import { FormServiceSaveHandlerApiRequest, FormServiceSaveHandlerEditableApiItem, FormServiceSaveHandlerFunction, FormServiceSaveHandlerNoop } from './form-service-save-handler';
import { FsOnAfterInitEvent, FsOnAfterSaveEvent, FsOnBeforeSaveEvent, FsOnExternalValueChangeEvent, FsOnUpdateFormControlReadonlyStateEvent } from './form-service.interfaces';
import { FormState } from './form-state';
export function isDynamicFormGroup(obj) {
  return obj instanceof DynamicFormGroup;
}
function resetAllContainedFormArrays(fg) {
  for (const key in fg.controls) {
    if (!{}.hasOwnProperty.call(fg.controls, key)) continue;
    const control = fg.controls[key];
    if (control instanceof UntypedFormArray || control instanceof FormArray) {
      control.clear();
    } else if (control instanceof UntypedFormGroup) {
      resetAllContainedFormArrays(control);
    }
  }
}
/**
 * BaseFormService is a generic model-driven form service.
 *
 * TODO:
 * - fix ExtractModelFromData / PartialData types somehow
 * - validationStatus$ should become partially distinct:
 *   validation from VALID to VALID should re-emit, but VALID and PENDING is all false and indistinguishable

 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// const test1:PartialData<{test:string}> = {test:'1'};
// const test2:PartialData<{test:string}[]> = [{test:'1'}, {}];
// const test3:{test:string}[] = [{test:'1'}, {}];
let id = 1;
export class BaseFormService {
  get formGroup() {
    return this.formGroup$.value;
  }
  get initialized() {
    return this.initialized$.value;
  }
  get initializedValue() {
    return this.initialized$.value;
  }
  get state$() {
    return this.state.getState$().pipe(takeUntilDestroyed(this.destroyRef));
  }
  get editMode() {
    return this.state.getState().editMode;
  }
  get formType() {
    return this.state.getState().formType;
  }
  get saveErrors$() {
    return this.events$.pipe(filter(e => e instanceof FsOnAfterSaveEvent), map(e => e.error), filter(err => !!err), takeUntilDestroyed(this.destroyRef));
  }
  get name() {
    const className = getClassName(this);
    const model = this.modelFactory?.Model;
    const modelName = model ? '::' + getClassName(model) : '';
    return `${className}${modelName} (${this._id})`;
  }
  get isRoot() {
    return this.formGroup && this.formGroup.root === this.formGroup;
  }
  // access required for saveHandlers
  getDestroyRef() {
    return this.destroyRef;
  }
  // warning: FormService does not use injection system, its dependencies are handled by ModelFormServiceFactory!
  constructor(dfb, formStateService, message, modelFactoryProvider, destroyRef, injector) {
    this.dfb = dfb;
    this.formStateService = formStateService;
    this.message = message;
    this.modelFactoryProvider = modelFactoryProvider;
    this.destroyRef = destroyRef;
    this.injector = injector;
    // semi-constants for controlling state durations.
    this.MIN_DURATION_SAVING = 500;
    this.MIN_DURATION_SAVED = 2000;
    // formGroup
    this.formGroup$ = new BehaviorSubject(null);
    this.initialized$ = new BehaviorSubject(false);
    this.initializedValue$ = new BehaviorSubject(false);
    this.usesExternalFormGroup = false; // if true it means that formGroup was not created by this formService
    // value setup
    // if value is patched before initialization, it will be cached here until setting it is possible
    this.initialValue = null;
    this.immutableValues = {};
    // stores the value as calculated by formService, without any changes done by formControls. Includes immtables and initialValue
    this.inputValue = null;
    // auto-persisted model properties will always be submitted even if they are not listed in editor definition.
    // only certain readonly props should be available in data for persisting.
    // for example readonly ID must be available for saving to work, but creationDate is readonly and should not be re-saved.
    // these special prop IDs are listed in this.persistableReadonlyProps.
    this.persistableReadonlyProps = ['id'];
    this.editorDefinitionName = DEFAULT_EDITOR_DEFINITION_ID;
    this.maxNestedModelDepth = 5;
    // required to cancel/notify pending async logic
    this._resetForm$ = new Subject();
    this.resettingForm$ = temporaryToggle(this._resetForm$, 100);
    // valueChanges and statusChanges are connected to the same-named observables of the currently managed form Group.
    // no piping/throttling or anything happens here.
    this.valueChanges$ = new Subject();
    this.statusChanges$ = new Subject();
    // use formValueChanges for behavior / user interaction. It is debounced etc
    // does NOT include readonly props!
    this._formValueChanges$ = new BehaviorSubject(null);
    this.formValueChanges$ = this._formValueChanges$.asObservable().pipe(filter(value => value !== null), takeUntilDestroyed(this.destroyRef));
    // events
    this.events$ = new Subject();
    // saving/submitting
    this.submitToApi = true; // can be disabled during debugging to stop submitting data to backend
    this.formSubscriptions = [];
    this._debug = false;
    this._id = id;
    id++;
    this.state = new FormState('' + id);
    this.setupObservables();
    this.destroyRef.onDestroy(() => {
      this.unbindFormGroupListeners();
    });
    if (!IS_PRODUCTION && PLATFORM !== 'jest') {
      setTimeout(() => {
        if (this.initialized) return;
        console.warn('it seems that a formService was created but not initialized.', this.modelFactory?.Model, this);
      }, 2000);
    }
    // console.log('created formService with id',this._id)
  }
  setupObservables() {
    this.formStateService.isBusy$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isBusy => {
      this.state.setBusy(isBusy);
    });
    // NOTE: during asynchronous logic chains like during saving or form reset, debouncing change updates will mess up everything. dont do it.
    // this is related to formGroup emitting valuechange event even when configurated otherwise.
    // setting the form's change state to true "by accident" will result in impacted UX.
    this.valueChanges$.pipe(distinctUntilChangedDeep({
      makeImmutable: true
    }), takeUntilDestroyed(this.destroyRef), startWith(null),
    // without, pairwise would swallow first value
    pairwise()).subscribe(([prev, next]) => {
      // we don't want initial value patching to trigger isChanged!
      if (prev === null) return;
      // skip isChanged update while resettingForm == true, as we do not want a form value reset to count as a change
      if (this.resettingForm$.value === true) return;
      this.state.setIsChanged(true);
    });
    this.valueChanges$.pipe(
    // skipUntil was added to delay first emission to make sure patchinitialmodel is already finished
    skipUntil(timer(10)), debounceTime(100), distinctUntilChangedDeep({
      makeImmutable: true
    }), takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      this._formValueChanges$.next(this.getValue());
    });
    // listen to statusChanges on regular formGroups
    // don't debounce or wait, statusChanges should be propagated immediately.
    this.statusChanges$.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(status => {
      if (this._debug) console.log(this.debugInfo() + ' statusChanges emitted -> state.setValidationStatus', status);
      this.state.setValidationStatus(status);
    });
  }
  debug() {
    if (!this._debug) {
      this._debug = true;
      console.log(this.debugInfo() + ' Start debugging formService', this);
      // console.log(this.debugInfo()+' form value at debug start '+this.getValue()) // can lead to errors if using defaultFormService!
      this.state$.subscribe(state => console.log(this.debugInfo() + ' state$ emitted', state));
      setTimeout(() => {
        if (!this.initialized) console.warn(this.debugInfo() + ' has not been initialized', this);
      }, 3000);
    }
  }
  debugInfo() {
    return `[DEBUG] ${this.name}`;
  }
  // ###########################################################################################################
  // ####  SETUP  ##############################################################################################
  // ###########################################################################################################
  setModel(input) {
    if (input instanceof EditorModelFactory) {
      this.setFactory(input);
    } else {
      if (!(input.prototype instanceof Model)) {
        throw new Error('Given Model Class does not inherit Model!');
      }
      const factory = this.modelFactoryProvider.createEditorModelFactory(input);
      this.setFactory(factory);
    }
    this.state.setName(this.name);
  }
  setFactory(factory) {
    this.modelFactory = factory;
  }
  setEditorDefinition(name) {
    this.editorDefinitionName = name;
  }
  getEditorDefinition() {
    return this.editorDefinitionName;
  }
  setEditMode(mode) {
    this.state.setEditMode(mode);
  }
  getEditMode() {
    return this.state.getState().editMode;
  }
  getEditMode$() {
    return this.state.getEditMode$();
  }
  // call after all wanted setup / setX methods have been called
  // TODO: calling initialized$ before applying initial value is not a good solution.
  // but when changing order, it works worse. need to figure this out...
  initialize() {
    var _this = this;
    return _asyncToGenerator(function* () {
      if (_this.initialized) {
        if (_this._debug) console.log(_this.debugInfo() + '.initialize skipped', _this);
        return Promise.resolve(false);
      }
      if (_this._debug) console.log(_this.debugInfo() + '.initialize started', _this);
      yield _this.buildFormGroup();
      if (_this._debug) console.log(_this.debugInfo() + '.initialize completed');
      if (_this._debug) console.log(_this.debugInfo() + ' patching initialValue ' + _this.initialValue);
      if (!IS_PRODUCTION && !_this.formGroup) {
        throw new Error('BaseFormService initialization failure: No formGroup available. Did buildFormGroup() fail?');
      }
      _this.initialized$.next(true);
      if (!_this.disableImmutableFormControls) _this.validateImmutableValues();
      _this.applyInitialValue();
      _this.initializedValue$.next(true);
      _this.events$.next(new FsOnAfterInitEvent());
      if (!IS_PRODUCTION) _this.warnIfDetectingDeprecatedAutoSaveHandler();
      return true;
    })();
  }
  /** reinitialization is required if EditorDefinition or EditMode changes */
  reinitialize() {
    if (this.usesExternalFormGroup) throw new Error('Cannot reinitialize formServices that are using externally set FormGroups!');
    this.unbindFormGroupListeners();
    this.formGroup$.next(null);
    this.initialized$.next(false);
    this.initializedValue$.next(false);
    this.initialize();
  }
  // ###########################################################################################################
  // ####  VALUE HANDLING  #####################################################################################
  // ###########################################################################################################
  getValue(includeReadonly = false) {
    if (!this.formGroup) return null;
    let value;
    // this.formGroup.value
    if (!includeReadonly) value = this.formGroup.value; // includes only editable props
    else value = this.formGroup.getRawValue(); // includes editable + readonly props
    if (Array.isArray(value)) throw new Error('BaseFormService.getValue does not support array data!');
    if (this.modelFactory?.Model) {
      value = this.modelFactory.fromData(value);
    }
    // add/lock readonly/immutable props to correct values
    // these must be applied AFTER Model transformation because the editorDefinition may not include immutableValue props.
    // but if immutableValues were set they must have higher priority than editorDefinition.
    value = this.applyImmutableValues(value);
    value = this.applyPersistableReadonlyProps(value);
    return value;
  }
  getValueIncludingReadonly() {
    return this.getValue(true);
  }
  hasValue() {
    const value = this.getValue();
    return !!value;
  }
  _setValue(inputValue, options = {}) {
    if (this._debug) console.log(this.debugInfo() + '._setValue', inputValue);
    if (Array.isArray(inputValue)) throw new Error('BaseFormService._setValue does not support array data!');
    let value;
    // TODO: should a difference check between current and new value happen?
    const initial = this.getInitialValue();
    if (!(inputValue instanceof Model) && !this.modelFactory) {
      // looks like a non-Model driven form
      value = this.applyImmutableValues({
        ...initial,
        ...inputValue
      });
    } else {
      if (!this.modelFactory) throw new Error('Cannot create value because modelFactory is missing!');
      // try to convert values to correct model
      if (!(inputValue instanceof this.modelFactory.Model)) {
        value = this.modelFactory.fromData(this.applyImmutableValues({
          ...initial,
          ...inputValue
        }));
        // console.log(this.debugInfo()+'._setValue applyImmutables -> fromData',value)
      } else {
        value = this.modelFactory.patch(inputValue, this.applyImmutableValues({}));
        // console.log(this.debugInfo()+'._setValue applyImmutables -> patch',value)
      }
    }
    this.reloadFormValue(value, options);
  }
  setInitialValue(inputValue) {
    if (this._debug) console.log(this.debugInfo() + '.setInitialValue (initialValue) ', inputValue);
    this.initialValue = inputValue;
    // Tif called post-initialization, the form needs to be recreated to reflect changed initialValue
    // if(this.initialized) this.reinitialize();
    // if(!this.initialized) {
    // 	throw Error('cannot set initial value after initialization!')
    // }
  }
  setValue(inputValue, options = {}) {
    if (!this.initialized) {
      throw Error('cannot set value before initialization! Use setInitialValue');
    } else {
      if (this._debug) console.log(this.debugInfo() + '.setValue ', inputValue);
      this._setValue(inputValue, options);
    }
  }
  patchValue(inputValue, options = {}) {
    // TODO: deep merge using class transformer
    this._setValue({
      ...this.getValue(true),
      ...inputValue
    }, options);
  }
  /**
   * this method tries to provide a solution for some issues occuring with formGroups & valueChanges & DynamicFormGroup.
   * - using patchValue/setValue will not emit a valueChange
   * - using formGroup.patchValue can emit a valueChange, but when done synchronously,
   *   the emitted value may be unexpected (experienced during implementing TaskSchedulerInputTaskActionComponent).
   * The most reliable and efficient way to do this seems to be asynchronously patching each individual control.
   */
  patchControlValues(inputValue, options) {
    var _this2 = this;
    return _asyncToGenerator(function* () {
      return new Promise(res => {
        setTimeout(() => {
          Object.entries(inputValue).forEach(([name, value]) => {
            const ctrl = _this2.formGroup.controls[name];
            ctrl.setValue(value, options);
          });
          res(true);
        });
      });
    })();
  }
  reloadFormValue(value, options = {}) {
    if (!this.initialized) {
      if (this._debug) console.log(this.debugInfo() + '.reloadFormValue [ignored due to calling pre-initialization]', value);
    } else {
      if (this._debug) console.log(this.debugInfo() + '.reloadFormValue post-initialize', value);
      this.inputValue = value;
      const fg = this.formGroup;
      if (fg instanceof UntypedFormArray) {
        throw new Error('FormArray is not supported by BaseFormService, must use ArrayFormService!');
      }
      if (isDynamicFormGroup(fg)) {
        // TODO: setObject will currently merge FormArray entries with existing ones.
        // to get a true SET object, we currently need to reset all formArrays found within the form before using setObject.
        // https://github.com/EndyKaufman/ngx-dynamic-form-builder/issues/195
        resetAllContainedFormArrays(fg);
        // formGroup setObject has no solution for passing in custom Class-transform options.
        // so there was need to inline the "setObject" function and modify it to use our own isntanceToPlain transformation.
        fg.setObject(this.modelFactoryProvider.classTransformService.instanceToPlain(value), options);
        // (fg as DynamicFormGroup<TData>).resetValidateAllFormFields();
        // NOTE: not 100% sure if updateValueAndValidity makes sense here, 
        // but as all others are calling updateValueAndValidity after setting a value, we do so too after calling .setObject.
        fg.updateValueAndValidity(options); // would make tests fail when using DFB v2!
      } else {
        // in case of Custom FormGroup, value may be incomplete.
        // so we can only use patchValue as missing properties would lead to errors using setValue.
        fg.patchValue(value);
        fg.updateValueAndValidity(options);
      }
      this.updateFormControlDisabledState();
    }
    this.events$.next(new FsOnExternalValueChangeEvent());
  }
  cloneFormValue(value) {
    if (Array.isArray(value)) return objectCloneDeep(value);else if (typeof value === 'object') return objectCloneDeep(value);
    throw Error('CloneFormValue failed: value is invalid: ' + value);
  }
  applyInitialValue() {
    // it is a bit delicate to decide if a default value should be patched or not.
    // we cannot _setValue([]) on FormArray - it would empty it.
    // A initialValue may exist, or not.
    // the formGroup may have been created during initialization or a custom one was passed to the service.
    // If a custom formgroup was passed, initialValue will be derived from it, but a custom initialValue could be set too.
    if (!this.dataIsArray()) {
      // will set initialValue, immutables, updates FormControlDisabled state
      // TODO: should not emit event, however this could result in bugs with existing
      // implementations that expect an initial emit
      this._setValue(this.dataIsArray() ? [] : {});
    }
  }
  // ###########################################################################################################
  // ####  EDITOR DEFINITION  ##################################################################################
  // ###########################################################################################################
  getEditorModelInfo() {
    return this.modelFactory.getEditorModelInfo();
  }
  getModelPropTypeInfo(name) {
    const modelInfo = this.getEditorModelInfo();
    if (modelInfo.nestedDefinitions.has(name)) {
      // a nested definition exists for this prop so it has to have a type too
      const nestedDefinitionName = modelInfo.nestedDefinitions.get(name);
      const meta = getSubModelMeta(this.modelFactory.Model, name);
      return {
        Model: meta.typeFunction(),
        isArray: meta.reflectedType === Array,
        definitionName: nestedDefinitionName
      };
    } else {
      return false;
    }
  }
  // ###########################################################################################################
  // ####  IMMUTABLES  #########################################################################################
  // ###########################################################################################################
  /**
   * locks controls to passed values.
   */
  setImmutableValues(immutables, disableImmutableFormControls = true) {
    this.immutableValues = {
      ...this.immutableValues,
      ...immutables
    };
    this.disableImmutableFormControls = disableImmutableFormControls;
    if (this.initialized) {
      // initial immutables will be checked during validation. 
      if (!disableImmutableFormControls) this.validateImmutableValues();
      if (disableImmutableFormControls) this.updateFormControlDisabledState();
      // make sure immutables are being patched
      this.patchValue({});
    }
  }
  validateImmutableValues() {
    if (!IS_PRODUCTION) {
      // assert that all passed immutable values are registered as readonly properties in used EditorDefinition
      if (this.modelFactory) {
        const readonlyPropNames = [...this.getEditorModelInfo().readonlyProps];
        for (const key in this.immutableValues) {
          if (Object.prototype.hasOwnProperty.call(this.immutableValues, key)) {
            const existsInReadonly = readonlyPropNames.includes(key);
            if (!existsInReadonly) console.warn("FormService misconfiguration: Property '" + key + "' was passed as immutable, but is not marked as readonly in EditorDefinition!");
          }
        }
      }
    }
  }
  // applyImmutableValues ensures that immutableValues will always be the same both when getting and setting values.
  applyImmutableValues(value) {
    // no cloning! would destroy model
    // const outValue = this.cloneFormValue(value);
    for (const key in this.immutableValues) {
      if ({}.hasOwnProperty.call(this.immutableValues, key)) {
        value[key] = this.immutableValues[key];
      }
    }
    return value;
  }
  // applyPersistableReadonlyProps ensures thatpersistableReadonlyProps will always be available in value both while setting and getting.
  // opposed to applyImmutableValues, these are only applied when getting a value, because setting a new value should update the inputValue instead of forcing existing data.
  // value may (when setting form value) or may not (when getting form value) include readonly props.
  // only certain readonly props should be available in data for persisting.
  // for example readonly ID must be available for saving to work, but creationDate is readonly and should not be re-saved.
  // these special prop IDs are listed in this.persistableReadonlyProps.
  applyPersistableReadonlyProps(value) {
    if (this.inputValue) {
      this.persistableReadonlyProps.forEach(name => {
        if (typeof this.inputValue[name] !== 'undefined') value[name] = this.inputValue[name];
      });
    }
    return value;
  }
  updateFormControlDisabledState() {
    if (!this.formGroup) return;
    // custom event hook allowing to manipulate formGroup controls state
    this.events$.next(new FsOnUpdateFormControlReadonlyStateEvent(this.formGroup));
    const readonlyPropNames = this.getAllReadonlyPropNames();
    if (!readonlyPropNames.length) return;
    const disableImmutables = group => {
      if (!(group instanceof UntypedFormGroup) || group instanceof UntypedFormArray) return;
      // iterate all formControls and set their state accordingly.
      for (const propName of readonlyPropNames) {
        group.controls[propName]?.disable();
      }
    };
    if (this.formGroup instanceof UntypedFormArray) {
      this.formGroup.controls.forEach(group => {
        disableImmutables(group);
      });
    } else {
      disableImmutables(this.formGroup);
    }
  }
  getAllReadonlyPropNames() {
    let readonlyPropNames = [];
    // editorDefinition readonly props
    if (this.modelFactory) {
      readonlyPropNames = [...this.getEditorModelInfo().readonlyProps];
    }
    // immutableValues are applied last, these have highest priority
    if (this.immutableValues && this.disableImmutableFormControls) {
      readonlyPropNames = readonlyPropNames.concat(Object.keys(this.immutableValues).filter(name => {
        return {}.hasOwnProperty.call(this.immutableValues, name);
      }));
    }
    return readonlyPropNames;
  }
  // ###########################################################################################################
  // ####  FORM GROUP  #########################################################################################
  // ###########################################################################################################
  getCrudAction() {
    let group;
    const mode = this.state.getState().editMode;
    if (mode === 'edit') group = CrudAction.UPDATE;else if (mode === 'new') group = CrudAction.CREATE;else if (mode === 'clone') group = CrudAction.CREATE;
    if (this._debug) console.log(this.debugInfo() + ' ValidationGroup', group);
    return group;
  }
  buildFormControlsConfig() {
    if (this._debug) console.log(this.debugInfo() + '.buildFormControlsConfig');
    const defaults = this.getDefaultValue();
    if (this._debug) console.log(this.debugInfo() + ' default model values:', defaults);
    const config = {};
    for (const k in defaults) {
      if (Object.prototype.hasOwnProperty.call(defaults, k)) config[k] = defaults[k];
    }
    return config;
  }
  // allows to delay form setup (buildForm) until required information is available.
  // used e.g. in CrudItemFormService
  resolveFormDependencies() {
    return Promise.resolve().then(() => {
      if (this._debug) console.log(this.debugInfo() + '.resolveFormDependencies done');
    });
  }
  // required to make form-array sub-forms work fine
  setFormGroup(form) {
    if (this._debug) console.log(this.debugInfo() + '.setFormGroup', form, form.value);
    if (this.dataIsArray()) throw Error('formService is in array mode, use setFormArray instead of setFormGroup!');
    const fg = this.enhanceFormGroup(form);
    this.formGroup$.next(fg);
    this.usesExternalFormGroup = true;
    // if a custom initialValue is already set, dont overwrite it!
    if (!this.initialValue) this.initialValue = this.cloneFormValue(form.value);
    this.bindFormGroupListeners();
  }
  setFormArray(form) {
    if (this._debug) console.log(this.debugInfo() + '.setFormArray', form, form.value);
    if (!this.dataIsArray()) throw Error('formService is in formgroup mode, use setFormGroup instead of setFormArray!');
    const fa = this.enhanceFormGroup(form);
    this.formGroup$.next(fa);
    this.usesExternalFormGroup = true;
    // if a custom initialValue is already set, dont overwrite it!
    if (!this.initialValue) this.initialValue = this.cloneFormValue(fa.value);
    this.bindFormGroupListeners();
  }
  buildFormGroup() {
    if (this._debug) console.log(this.debugInfo() + '.buildFormGroup', this.modelFactory, this);
    if (this.formGroup) return Promise.resolve();
    if (!this.modelFactory) return Promise.reject('modelFactory is not available');
    this.configurateFactory();
    if (this._debug) console.log(this.debugInfo() + '.buildFormGroup loaded EditorDefinition', this.editorDefinitionName, this.modelFactory.Model);
    return this.resolveFormDependencies().then(() => {
      const fg = this.createDynamicFormGroupAsGeneric();
      this.formGroup$.next(fg);
      if (this._debug) console.log(this.debugInfo() + ' created formGroup: ', this.formGroup);
      // this.formGroup.reset(); // would reset state but value too!
      this.formGroup.markAsPristine();
      this.formGroup.markAsUntouched();
      this.bindFormGroupListeners();
    });
  }
  configurateFactory() {
    this.modelFactory.setEditorDefinitionName(this.editorDefinitionName);
    this.modelFactory.setGroups([this.getCrudAction()]);
  }
  enhanceFormGroup(fg) {
    const fgWithService = fg;
    fgWithService.formService = this;
    return fgWithService;
  }
  resetFormState(config = {}) {
    this.formGroup.markAsPristine();
    this.formGroup.markAsUntouched();
    const keepEditMode = typeof config.keepEditMode === 'boolean' ? config.keepEditMode : true;
    this.state.reset({
      keepEditMode
    });
    this.resetFormFieldValidation();
  }
  resetForm(config = {}) {
    this._resetForm$.next();
    if (config.value) {
      // or use reloadFormValue? which is better in this situation?
      // this.reloadFormValue(config.value);
      this.setInitialValue(config.value);
      config.resetValue = true;
    }
    if (config.resetValue) {
      // resets state + nulls all controls
      this.formGroup.reset({
        emitEvent: false
      });
      // better than reloading initialValue as it includes immutables and class-transformer
      this._setValue({}, {
        emitEvent: false
      });
    }
    this.resetFormState();
  }
  registerSubForm(form, param) {
    // TODO: seems unused, remove soon
    throw new Error('registerSubForm called. unclear if this is still in use.');
    // if(this._debug) console.log(this.debugInfo()+'.registerSubForm',param,form)
    // const fg = (this.formGroup as FormGroup);
    // const oldControl = fg.controls[param];
    // fg.removeControl(param);
    // fg.addControl(param,form);
    // form.patchValue(oldControl.value);
  }
  bindFormGroupListeners() {
    if (this._debug) console.log(this.debugInfo() + '.bindFormGroupListeners', this.formGroup);
    this.unbindFormGroupListeners();
    this.formSubscriptions.push(this.formGroup.valueChanges.subscribe(data => {
      this.valueChanges$.next(data);
    }), this.formGroup.statusChanges.subscribe(data => {
      this.statusChanges$.next(data);
    })
    // this.formGroup.events.subscribe(e=>{
    // 	if(e instanceof ValueChangeEvent) {
    // 		this.valueChanges$.next(e.value);
    // 	}
    // 	if(e instanceof StatusChangeEvent) {
    // 		this.statusChanges$.next(e.status);
    // 	}
    // }),
    );
    // statusChanges will not emit immediately, so propagate current status to isValid
    // TODO: can use startWith pipe?
    this.state.setValidationStatus(this.formGroup.status);
  }
  unbindFormGroupListeners() {
    this.formSubscriptions.forEach(subscr => subscr.unsubscribe());
    this.formSubscriptions = [];
  }
  createDynamicFormGroupAsGeneric() {
    if (this._debug) console.log(this.debugInfo() + '.buildFormGroup', this.modelFactory.Model, this.buildFormControlsConfig());
    if (this.dataIsArray()) {
      throw new Error('this method must not be used with FormArray mode!');
    }
    const fg = this.createDynamicFormGroup(this.buildFormControlsConfig());
    return this.enhanceFormGroup(fg);
  }
  // does not work with FormArray, createDynamicFormGroupAsGeneric is a utility allowing for easy use with TFormGroup
  // NOTE: data must only contain primitive values!
  // Angular will treat arrays as a configuration structure here!
  createDynamicFormGroup(data = {}) {
    const model = this.modelFactory.Model;
    const modelInfo = this.getEditorModelInfo();
    const allowedNestedModels = modelInfo.nestedModelProps;
    if (this._debug) console.log(this.debugInfo() + ' createDynamicFormGroup - editorModel name / allowedNestedModels:', modelInfo.name, allowedNestedModels, modelInfo.definition.data);
    const form = this.dfb.dynamicGroup(model, data, {
      classValidatorOptions: {
        ...CLASS_VALIDATOR_GLOBAL_CONFIG_FORMBUILDER,
        groups: [this.getCrudAction()]
      },
      classTransformOptions: this.getClassTransformOptions(model, data),
      maxNestedModelDepth: this.maxNestedModelDepth,
      allowedNestedModels
    });
    if (Object.keys(data).length) form.setObject(data);
    if (this._debug) console.log(this.debugInfo() + ' createDynamicFormGroup - result:', form.controls);
    return form;
  }
  getClassTransformOptions(model, data) {
    return this.modelFactoryProvider.classTransformService.createOptionsFor(model, data, 'toInstance', {
      groups: [this.getCrudAction()]
    });
  }
  // ###########################################################################################################
  // ####  FORM STATE + VALIDATION  ############################################################################
  // ###########################################################################################################
  markAllTouched(form) {
    this._applyFuncToAllControls(form, control => {
      control.markAsTouched({
        onlySelf: true
      });
    }, true);
  }
  markAllUntouched(form) {
    this._applyFuncToAllControls(form, control => {
      control.markAsUntouched({
        onlySelf: true
      });
    }, true);
  }
  markAllDirty(form) {
    this._applyFuncToAllControls(form, control => {
      control.markAsDirty({
        onlySelf: true
      });
    }, true);
  }
  /**
   * updateValueAndValidity will not revalidate children, only ancestors can be re-run.
   * So to force full revalidation it must be called for all controls individually
   */
  revalidateAll(form) {
    this._applyFuncToAllControls(form, control => {
      control.updateValueAndValidity({
        onlySelf: true,
        emitEvent: true
      });
    }, true);
  }
  _applyFuncToAllControls(form, func, callFuncForGroupsToo = false) {
    if (form instanceof UntypedFormArray) {
      form.controls.forEach(control => {
        if (control instanceof UntypedFormControl) func(control);else this._applyFuncToAllControls(control, func, callFuncForGroupsToo); // if its no FormControl, it must be FormGroup|FormArray
      });
    } else if (form instanceof UntypedFormGroup) {
      for (const k in form.controls) {
        if (!Object.prototype.hasOwnProperty.call(form.controls, k)) continue;
        const control = form.get([k]); // pass as array to support field names containing dots
        if (control instanceof UntypedFormControl) func(control);else this._applyFuncToAllControls(control, func, callFuncForGroupsToo); // if its no FormControl, it must be FormGroup|FormArray
      }
    }
    if (callFuncForGroupsToo) func(form);
  }
  resetFormFieldValidation() {
    this.markAllUntouched(this.formGroup);
    this.formGroup.updateValueAndValidity({
      emitEvent: false
    });
  }
  // will re-validate the whole formGroup structure and "attach" validation logic for newly added children
  refreshFormState() {
    this.formGroup.root.resetValidateAllFormFields();
  }
  // ###########################################################################################################
  // ####  API / SAVING  #######################################################################################
  // ###########################################################################################################
  // TODO: AutoSave + EditMode should be part of ModelFormService only?
  enableAutoSave(enable = true) {
    // if(this.autoSaveSubscription) {
    // 	this.autoSaveSubscription.unsubscribe()
    // }
    // if(enable) {
    // 	this.autoSaveSubscription = this._formValueChanges$.asObservable().pipe(
    // 		skip(1),
    // 		debounceTime(400)
    // 	).subscribe(value=>{
    // 		console.log(this.debugInfo()+' autosave triggered, current form value is:',value)
    // 		if(this.editMode === 'edit') this.save()
    // 	})
    // } else {
    // 	this.autoSaveSubscription = undefined;
    // }
  }
  setSaveHandler(input, configFunc) {
    if (input === 'noop') {
      // as a shortcut, setSaveHandler('noop') can be called to create a dummy save handler.
      // this will prevent auto-assignment of a functional saveHandler (or error in case none could be determined.)
      this.saveHandler = new FormServiceSaveHandlerNoop(this, this.injector);
    } else if (typeof input === 'function') {
      this.saveHandler = new input(this, this.injector);
    } else {
      this.saveHandler = input;
    }
    if (configFunc) {
      configFunc(this.saveHandler);
    }
  }
  setSaveHandlerFunction(func) {
    this.saveHandler = new FormServiceSaveHandlerFunction(this, this.injector, func);
  }
  getSaveHandler() {
    if (!this.saveHandler) {
      // default behavior: try to submit to api
      const handler = this.determineAppropriateSaveHandler();
      this.setSaveHandler(handler);
    }
    return this.saveHandler;
  }
  // as determineAppropriateSaveHandler is only called on submit normally,
  // we add an extra call so that the warning is visible early in dev env.
  warnIfDetectingDeprecatedAutoSaveHandler() {
    if (!IS_PRODUCTION) setTimeout(() => {
      if (!this.saveHandler) {
        try {
          this.determineAppropriateSaveHandler();
        } catch (err) {}
      }
    }, 1000);
  }
  determineAppropriateSaveHandler() {
    const definitionOptions = this.getEditorModelInfo().definition.options;
    if (definitionOptions.useCrudEndpoint) {
      if (!IS_PRODUCTION) {
        console.warn(this);
        throw new Error('ERROR: Relying on auto-determined FormServiceSaveHandlerEditableApiItem SaveHandler is not supported anymore. Please call setSaveHandler explicitly with either FormServiceSaveHandlerEditableApiItem or FormServiceSaveHandlerSubmitOnlyApiItem.');
      }
      return FormServiceSaveHandlerEditableApiItem;
    } else if (definitionOptions.useAppRequest) {
      return FormServiceSaveHandlerApiRequest;
    } else {
      throw new Error('determineAppropriateSaveHandler failed. No appropriate handler could be found. Please pass a handler to formService.setSaveHandler manually.');
    }
  }
  save(subscriber) {
    this.state.setHasTriedToSubmitForm(true);
    const saveExecutor = this.validateAllFormFields(true).pipe(switchMap(state => {
      if (this._debug) console.log(this.debugInfo() + ' pre-save', state, this.formGroup['classValidatorErrors'], this.formGroup.status);
      const value = this.getValue();
      if (!state.valid || state.saveState === 'saving') {
        return of(false);
      }
      this.events$.next(new FsOnBeforeSaveEvent(value));
      if (this._debug) console.log(this.debugInfo() + ' data to submit: ', value);
      return this.createSaveObservable(value);
    }),
    // just to be sure that the observable will be completed and unsubscribed.
    take(1)
    // no need to share() here, this is done within createSaveObservable
    );
    // subscribing here triggers execution of save logic eagerly.
    // this will make sure saving it executed even if no more subscribers are being added. 
    // howwever, error handling is complicated because of different situations:
    // - if auto-subscriber adds dummy error handler:
    // - client may subscribe to saveExecutor with specifying error handler => error thrown once
    // - client may subscribe without specifying error handler => errors will be swallowed
    // - if auto-subscriber adds no dummy error handler:
    // - client may subscribe to saveExecutor with specifying error handler => error thrown twice
    // - client may subscribe without specifying error handler => errors thown once
    if (subscriber) {
      if (typeof subscriber === 'object') {
        saveExecutor.subscribe({
          error: err => this.defaultSaveErrorHandler(err),
          ...subscriber
        });
      } else {
        saveExecutor.subscribe({
          next: subscriber,
          error: err => this.defaultSaveErrorHandler(err)
        });
      }
    } else {
      // no subscriber was passed, need to subscribe without catching errors.
      saveExecutor.subscribe({
        error: err => this.defaultSaveErrorHandler(err)
      });
    }
  }
  defaultSaveErrorHandler(err) {
    throw err;
  }
  getValidityAndValue() {
    this.state.setHasTriedToSubmitForm(true);
    const tmp = this.validateAllFormFields(true).pipe(switchMap(state => {
      const value = this.getValue();
      return of({
        value,
        isValid: state.valid
      });
    }), share());
    tmp.subscribe();
    return tmp;
  }
  // TODO: should save functionality + validateAllFormFields only be part of ModelFormService?
  validateAllFormFields(emitEvent = true) {
    if (this._debug) console.log(this.debugInfo() + '.validateAllFormFields');
    this.markAllTouched(this.formGroup);
    this.markAllDirty(this.formGroup);
    // TODO: required call? support formArrays?
    if (this.formGroup instanceof DynamicFormGroup) this.formGroup.validateAllFormFields();
    // if((this.formGroup as any) instanceof UntypedFormGroup) validateAllFormFields(this.formGroup as unknown as FormGroup);
    this.revalidateAll(this.formGroup);
    let source;
    if (this.formGroup.status === 'PENDING') {
      source = this.formGroup.statusChanges.pipe(
      // tap(()=>console.log('statuschange',this.formGroup.status)),
      skipWhile(status => status === 'PENDING'), take(1));
    } else {
      source = of(this.formGroup.status);
    }
    const runner = source.pipe(
    // tap(console.log),
    switchMap(() => this.state$), take(1), share());
    runner.subscribe();
    return runner;
    // // dynamicFormBuilder/classValidator runs validation eagerly but asynchronously.
    // const asyncValidationSource = this.formGroup.statusChanges.pipe(
    // 	// tap(()=>console.log('statuschange',this.formGroup.status)),
    // 	skipWhile(status=>status==='PENDING')
    // );
    // // angular validators can also be synchonous in which case statusChanges would not re-emit if state has not changed.
    // const timeoutValidationSource = timer(50).pipe(
    // 	// tap(()=>console.log('timout',this.formGroup.status)),
    // 	map(()=>this.formGroup.status), 
    // 	filter(status=>status!=='PENDING')
    // );
    // const source = isDynamicFormGroup(this.formGroup) 
    // 	? asyncValidationSource 
    // 	: merge(asyncValidationSource,timeoutValidationSource);
  }
  createSaveObservable(value) {
    const handler = this.getSaveHandler();
    const request = handler.execute(value);
    this.state.setSaveState('saving');
    const boundRequest = this.bindSubmissionToFormState(request);
    return boundRequest;
  }
  // req observable returns either TData (when used with ModelService)
  // or AppRequest response (= RequestResponse<TData>) (when used with AppRequestService)
  bindSubmissionToFormState(req) {
    const startTime = Date.now();
    const handler = req.pipe(
    // add a min-length for saving so that UI does not flicker too much
    delayWhen(() => timer(startTime - Date.now() + this.MIN_DURATION_SAVING)), catchError(error => {
      this.state.setSaveState('idle');
      this.events$.next(new FsOnAfterSaveEvent({
        error
      }));
      return throwError(() => error);
    }), tap(result => {}), switchMap(responseData => {
      return this.state.getSaveState$()
      // .pipe(skip(1)) // skip first emission (immediate due to being a subject)
      .pipe(take(1)) // subscribe only once (to the SECOND emission)
      .pipe(tap(val => {
        // NOTE: important to execute BEFORE resetting state, as setValue + FsOnAfterSaveEvent might trigger value changes					
        if (this.saveHandler.returnsUpdatedFormData) {
          this.setValue(responseData, {
            emitEvent: false
          });
          const item = this.modelFactory.fromData(responseData);
          this.events$.next(new FsOnAfterSaveEvent({
            responseData,
            item
          }));
        } else {
          this.events$.next(new FsOnAfterSaveEvent({
            responseData
          }));
        }
        this.state.setSaveState('saved');
        this.state.setHasTriedToSubmitForm(false);
        this.state.setIsChanged(false);
        // go to idle state after MIN_DURATION_SAVED or if isChanged status changes
        race([timer(this.MIN_DURATION_SAVED), this.state.getIsChanged$().pipe(filter(state => state === true))]).pipe(switchMap(() => this.state$), take(1)).subscribe(currentState => {
          // no matter if editor has changes or not, the saveState must be reset to idle as saving/saved would take precedence! 
          // however, if saveState has already changed to something other than "saved", do not overwrite it.
          if (currentState.saveState === 'saved') this.state.setSaveState('idle');
        });
      }));
    }), share());
    return handler;
  }
}